import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FaBars, FaTimes, FaEnvelope, FaMoon, FaSun, FaChevronDown } from 'react-icons/fa'
import styles from './Header.module.css'

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [fontSize, setFontSize] = useState('medium')
  const location = useLocation()

  useEffect(() => {
    const isDark = localStorage.getItem('darkMode') === 'true'
    setDarkMode(isDark)
    if (isDark) {
      document.body.classList.add('dark-mode')
    }
    
    const savedFontSize = localStorage.getItem('fontSize') || 'medium'
    setFontSize(savedFontSize)
    document.documentElement.setAttribute('data-font-size', savedFontSize)
  }, [])

  const toggleDarkMode = () => {
    const newMode = !darkMode
    setDarkMode(newMode)
    localStorage.setItem('darkMode', newMode)
    document.body.classList.toggle('dark-mode')
  }

  const cycleFontSize = () => {
    const sizes = ['small', 'medium', 'large']
    const currentIndex = sizes.indexOf(fontSize)
    const nextIndex = (currentIndex + 1) % sizes.length
    const newSize = sizes[nextIndex]
    setFontSize(newSize)
    localStorage.setItem('fontSize', newSize)
    document.documentElement.setAttribute('data-font-size', newSize)
  }

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  const closeMobileMenu = () => {
    setMobileMenuOpen(false)
  }

  const isActive = (path) => {
    return location.pathname === path
  }

  return (
    <nav className={styles.nav} role="navigation" aria-label="Hoofdnavigatie">
      <div className={`container ${styles.navContainer}`}>
        <div className={styles.navContent}>
          {/* Logo */}
          <Link 
            to="/" 
            className={styles.logo}
            aria-label="Sobé Zorg Twente - Ga naar homepagina"
            onClick={closeMobileMenu}
          >
            <img 
              src="/assets/img/sobe-logo.png" 
              alt="Sobé Zorg Twente Logo" 
              className={styles.logoImg}
              onError={(e) => {
                e.target.onerror = null
                e.target.src = '/assets/img/sobe-logo-placeholder.svg'
              }}
            />
            <div>
              <h1 className={styles.logoTitle}>Sobé Zorg Twente</h1>
              <p className={styles.logoSubtitle}>Begeleiding voor Ouderen</p>
            </div>
          </Link>

          {/* Desktop Menu */}
          <ul className={styles.desktopMenu} role="menubar">
            <li role="none">
              <Link 
                to="/" 
                className={`${styles.navLink} ${isActive('/') ? styles.active : ''}`}
                role="menuitem"
              >
                Home
              </Link>
            </li>
            <li role="none">
              <Link 
                to="/over-ons" 
                className={`${styles.navLink} ${isActive('/over-ons') ? styles.active : ''}`}
                role="menuitem"
              >
                Over ons
              </Link>
            </li>
            <li role="none" className={styles.dropdown}>
              <Link 
                to="/diensten" 
                className={`${styles.navLink} ${isActive('/diensten') ? styles.active : ''}`}
                role="menuitem"
                aria-haspopup="true"
              >
                Diensten <FaChevronDown className={styles.chevron} aria-hidden="true" />
              </Link>
              <div className={styles.dropdownMenu} role="menu">
                <Link to="/diensten#dagbesteding" role="menuitem" onClick={closeMobileMenu}>
                  Dagbesteding
                </Link>
                <Link to="/diensten#begeleiding" role="menuitem" onClick={closeMobileMenu}>
                  Individuele Begeleiding
                </Link>
                <Link to="/diensten#vervoer" role="menuitem" onClick={closeMobileMenu}>
                  Vervoer
                </Link>
              </div>
            </li>
            <li role="none">
              <Link 
                to="/werk-en-leren" 
                className={`${styles.navLink} ${isActive('/werk-en-leren') ? styles.active : ''}`}
                role="menuitem"
              >
                Werk & Leren
              </Link>
            </li>
            <li role="none">
              <Link 
                to="/contact" 
                className={`${styles.navLink} ${isActive('/contact') ? styles.active : ''}`}
                role="menuitem"
              >
                Contact
              </Link>
            </li>
            <li role="none">
              <Link 
                to="/contact" 
                className={styles.btnPrimary}
                aria-label="Neem contact met ons op"
              >
                <FaEnvelope aria-hidden="true" /> Contact
              </Link>
            </li>
            <li role="none">
              <button 
                onClick={cycleFontSize}
                className={styles.fontSizeToggle}
                aria-label={`Huidige tekstgrootte: ${fontSize}. Klik om te wijzigen`}
                title="Tekstgrootte aanpassen"
              >
                <span className={styles.fontSizeIcon}>
                  <span style={{ fontSize: '0.8em' }}>A</span>
                  <span style={{ fontSize: '1.2em' }}>A</span>
                </span>
              </button>
            </li>
            <li role="none">
              <button 
                onClick={toggleDarkMode}
                className={styles.darkModeToggle}
                aria-label="Schakel donkere modus in of uit"
              >
                {darkMode ? <FaSun aria-hidden="true" /> : <FaMoon aria-hidden="true" />}
              </button>
            </li>
          </ul>

          {/* Mobile Menu Button */}
          <button 
            className={styles.mobileMenuBtn}
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? 'Sluit mobiele navigatie' : 'Open mobiele navigatie'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div id="mobile-menu" className={styles.mobileMenu} role="menu">
            <ul>
              <li role="none">
                <Link to="/" role="menuitem" onClick={closeMobileMenu}>Home</Link>
              </li>
              <li role="none">
                <Link to="/over-ons" role="menuitem" onClick={closeMobileMenu}>Over ons</Link>
              </li>
              <li role="none">
                <Link to="/diensten" role="menuitem" onClick={closeMobileMenu}>Diensten</Link>
              </li>
              <li role="none">
                <Link to="/werk-en-leren" role="menuitem" onClick={closeMobileMenu}>Werk & Leren</Link>
              </li>
              <li role="none">
                <Link to="/contact" role="menuitem" onClick={closeMobileMenu}>Contact</Link>
              </li>
            </ul>
          </div>
        )}
      </div>
    </nav>
  )
}

export default Header
